/**
 * Which screen the QR session opens onto (PRD GST-001, GST-028, GST-040).
 *
 * This is a pure function of the query's state rather than a chain of booleans
 * inside the gate component, because the failure it exists to prevent is not
 * visible in any single boolean: a resolve that is *unsettled but not running*.
 *
 * TanStack Query's `status` stays `pending` for the whole life of a query that
 * has never produced data - while it is fetching, while a retry is paused
 * because the browser reports itself offline, and after the fetch was cancelled.
 * Reading `isPending` as "we are loading" therefore paints the resolving spinner
 * over two states in which nothing will ever arrive, and the guest is left on it
 * forever with a single request in the server log and nothing in the console.
 * `fetchStatus` is what separates them, so the decision is made on the pair.
 *
 * But fetch mechanics alone cannot say *what went wrong*, and that is the second
 * thing this function exists to get right. "This code did not open" and "we
 * could not reach the server" are opposite diagnoses with opposite remedies, and
 * deciding between them from `status`/`fetchStatus` decides them from the wrong
 * evidence: a code the worker had already refused with 404 read as UNREACHABLE
 * for as long as the query had not settled, and the guest was told to check a
 * connection that was fine and given a retry that could never succeed. So the
 * recorded failure is consulted first - it is the most recent statement of fact
 * about the code - and the fetch mechanics only decide the cases where nothing
 * has failed yet.
 *
 * The other rule encoded here: once a session has resolved, a later failure of a
 * background refetch must not eject a guest who is mid-order. A resolved session
 * outranks a transient error.
 */

/** Mirrors TanStack Query's `status`, without importing it into a pure module. */
export type ResolveStatus = 'pending' | 'error' | 'success';

/** Mirrors TanStack Query's `fetchStatus`. */
export type ResolveFetchStatus = 'fetching' | 'paused' | 'idle';

export type SessionScreen =
  /** No token at all: tell the guest where the code is. */
  | 'SCAN'
  /** A token exists and the resolve is genuinely in flight. */
  | 'RESOLVING'
  /**
   * We never got an answer: the resolve failed in transport, or it is neither
   * running nor settled - paused offline, or cancelled. Honest copy plus a
   * retry that can actually succeed; never an unattended spinner.
   */
  | 'UNREACHABLE'
  /** The server answered and refused the code: unknown, forged, revoked, expired. */
  | 'INVALID'
  | 'NOT_SERVABLE'
  | 'SERVICE_PAUSED'
  /**
   * PRD OPS-025 / GST-042 - neither pre-order nor immediate delivery is
   * switched on, so there is nothing anybody can order anywhere. Its own screen
   * rather than the paused one: a pause is temporary and says so, and telling a
   * guest ordering is "paused" when the platform is simply not selling would be
   * a promise nobody made. Orders already placed stay reachable behind it.
   */
  | 'ORDERING_CLOSED'
  | 'STOREFRONT';

/**
 * How the last resolve attempt failed, if one has.
 *
 * `REJECTED` is the server refusing this code; `UNREACHABLE` is never having
 * got a usable answer. Classified in `lib/api` from the error itself, so this
 * module stays free of the transport it is describing.
 */
export type ResolveFailure = 'REJECTED' | 'UNREACHABLE';

export interface SessionScreenInput {
  readonly qrToken: string | null;
  readonly status: ResolveStatus;
  readonly fetchStatus: ResolveFetchStatus;
  readonly session: {
    readonly servable: boolean;
    readonly servicePaused: boolean;
    /**
     * PRD OPS-025 - required, never optional: the session always states it, and
     * an optional field here would let a caller that forgot it fall silently
     * through to the storefront on a platform selling nothing.
     */
    readonly orderingModes: { readonly preorder: boolean; readonly immediate: boolean };
  } | null;
  /**
   * The last resolve failure, from the settled error or from the failure that
   * is currently being retried. Null until something has actually failed.
   */
  readonly failure?: ResolveFailure | null;
  /**
   * True once a resolve has been in flight longer than the platform's patience
   * deadline - `RESOLVE_PATIENCE_MS` in `@croissants/ui`, measured by `useStalled`
   * there. This module states no deadline of its own; a second one is a second
   * answer to "how long does a reader wait", and the guest's was the longest.
   */
  readonly resolveStalled?: boolean;
}

export function sessionScreen(input: SessionScreenInput): SessionScreen {
  if (input.qrToken === null) return 'SCAN';

  // A resolved session wins over a failing refetch: a guest half way through an
  // order is not thrown back to a landing screen by one dropped request.
  if (input.session !== null) {
    if (!input.session.servable) return 'NOT_SERVABLE';
    // The kill switch is SENIOR to both modes (CON-008 / OPS-009, OPS-025): the
    // modes say what the platform sells, the pause says whether it is open.
    if (input.session.servicePaused) return 'SERVICE_PAUSED';
    const modes = input.session.orderingModes;
    if (!modes.preorder && !modes.immediate) return 'ORDERING_CLOSED';
    return 'STOREFRONT';
  }

  // The server already answered, and its answer was about the code. Nothing the
  // query does afterwards - a retry still in flight, a retry paused offline, a
  // cancelled fetch that leaves the query pending forever - can make that code
  // valid, so this decision is taken before any of it is looked at.
  if (input.failure === 'REJECTED') return 'INVALID';

  // Settled without data and without a classified failure: still a dead end for
  // this code, and the invalid screen is the one with the rescan on it.
  if (input.status === 'error') return input.failure === 'UNREACHABLE' ? 'UNREACHABLE' : 'INVALID';
  // `success` with no session cannot happen through the contract, but it must
  // still land somewhere with a way out rather than on the spinner.
  if (input.status === 'success') return 'INVALID';
  if (input.fetchStatus !== 'fetching') return 'UNREACHABLE';
  return input.resolveStalled === true ? 'UNREACHABLE' : 'RESOLVING';
}
